import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { AdminGuard } from "@/components/admin/AdminGuard";
import { ActiveBadge } from "@/components/admin/ActiveBadge";
import { DeactivateUserDialog } from "@/components/admin/DeactivateUserDialog";
import { DeleteUserDialog } from "@/components/admin/DeleteUserDialog";
import { EditUserForm } from "@/components/admin/EditUserForm";
import { RelativeTime } from "@/components/admin/RelativeTime";
import { ResetPasswordDialog } from "@/components/admin/ResetPasswordDialog";
import { RoleBadge } from "@/components/admin/RoleBadge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useAdminUser,
  useUpdateAdminUser,
  type AdminUser,
} from "@/queries/adminUsers";
import { useMe } from "@/queries/auth";

export const Route = createFileRoute("/admin/users/$id")({
  component: AdminUserDetailRoute,
});

function AdminUserDetailRoute() {
  return (
    <AdminGuard>
      <DetailBody />
    </AdminGuard>
  );
}

function DetailBody() {
  const { t } = useTranslation("admin-users");
  const navigate = useNavigate();
  const { id } = useParams({ from: "/admin/users/$id" });
  const { data: me } = useMe();
  const { data: user, isPending, isError } = useAdminUser(id);

  const [resetOpen, setResetOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isPending) {
    return <p className="p-6 text-sm text-muted-foreground">{t("detail.loading")}</p>;
  }
  if (isError || !user) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{t("detail.notFound")}</AlertDescription>
        </Alert>
        <div className="mt-4">
          <BackLink />
        </div>
      </div>
    );
  }

  const isSelf = me?.id === user.id;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <BackLink />

      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold">{user.email}</h1>
          <RoleBadge role={user.role} />
          <ActiveBadge isActive={user.is_active} />
          {isSelf ? (
            <span className="text-xs text-muted-foreground">{t("row.youSelf")}</span>
          ) : null}
        </div>
        {user.display_name ? (
          <p className="text-sm text-muted-foreground">{user.display_name}</p>
        ) : null}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t("detail.metadata.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailMetadata user={user} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("detail.sectionEdit")}</CardTitle>
        </CardHeader>
        <CardContent>
          <EditUserForm user={user} isSelf={isSelf} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("detail.sectionDanger")}</CardTitle>
          <CardDescription>{t("detail.dangerHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setResetOpen(true)}>
              {t("detail.actions.resetPassword")}
            </Button>
            {user.is_active ? (
              <Button
                variant="outline"
                onClick={() => setDeactivateOpen(true)}
                disabled={isSelf}
              >
                {t("detail.actions.deactivate")}
              </Button>
            ) : (
              <ReactivateButton user={user} />
            )}
            <Button
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={isSelf}
            >
              {t("detail.actions.delete")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {resetOpen ? (
        <ResetPasswordDialog open onOpenChange={setResetOpen} user={user} />
      ) : null}
      {deactivateOpen ? (
        <DeactivateUserDialog open onOpenChange={setDeactivateOpen} user={user} />
      ) : null}
      {deleteOpen ? (
        <DeleteUserDialog
          open
          onOpenChange={setDeleteOpen}
          user={user}
          onDeleted={() => void navigate({ to: "/admin/users" })}
        />
      ) : null}
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation("admin-users");
  return (
    <Link
      to="/admin/users"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {t("detail.back")}
    </Link>
  );
}

function DetailMetadata({ user }: { user: AdminUser }) {
  const { t } = useTranslation("admin-users");
  const rows: Array<[string, React.ReactNode]> = [
    [t("detail.metadata.id"), <code className="text-xs">{user.id}</code>],
    [t("detail.metadata.createdAt"), <RelativeTime iso={user.created_at} />],
    [t("detail.metadata.updatedAt"), <RelativeTime iso={user.updated_at} />],
    [t("detail.metadata.lastLoginAt"), <RelativeTime iso={user.last_login_at} fallbackKey="detail.metadata.never" />],
    [
      t("detail.metadata.deactivatedAt"),
      user.deactivated_at ? (
        <RelativeTime iso={user.deactivated_at} />
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    ],
    [
      t("detail.metadata.mustChangePassword"),
      user.must_change_password
        ? t("detail.metadata.yes")
        : t("detail.metadata.no"),
    ],
  ];
  return (
    <dl className="grid gap-2 text-sm sm:grid-cols-[180px_1fr]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ReactivateButton({ user }: { user: AdminUser }) {
  const { t } = useTranslation("admin-users");
  const update = useUpdateAdminUser(user.id);
  return (
    <Button
      variant="outline"
      onClick={() => update.mutate({ is_active: true })}
      disabled={update.isPending}
    >
      {t("detail.actions.reactivate")}
    </Button>
  );
}
